// SurchargesTab.js - Conditional surcharges (shape, glass, etc.)
import {
  React,
  useEffect,
  useState,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC
} from './PricingCommon';
import { useProductScope } from '../utils/productScope';

const SurchargesTab = () => {
  const [surcharges, setSurcharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const { canSeeScope } = useProductScope();

  const fetchData = () => {
    setLoading(true);
    authFetch('/pricing/surcharges')
      .then(data => { setSurcharges(data); setLoading(false); })
      .catch(err => { console.error('Failed to fetch surcharges:', err); setLoading(false); });
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (formData) => {
    try {
      let updatedRow;
      if (editData?.id) {
        updatedRow = await authFetchRequest(`/pricing/surcharges/${editData.id}`, formData, 'PUT');
        setSurcharges(prev => prev.map(row => row.id === editData.id ? updatedRow : row));
        if (selectedRow?.id === editData.id) setSelectedRow(updatedRow);
      } else {
        updatedRow = await authFetchRequest('/pricing/surcharges', formData, 'POST');
        setSurcharges(prev => [...prev, updatedRow]);
      }
      setOpenDialog(false);
      setEditData(null);

    } catch (err) {
      console.error('Save failed:', err);
      alertC('Failed to save surcharge');
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const confirmed = await confirmC('Are you sure you want to delete this surcharge?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/pricing/surcharges/${selectedRow.id}`, null, 'DELETE');
      setSurcharges(prev => prev.filter(row => row.id !== selectedRow.id));
      setSelectedRow(null);

    } catch (err) {
      console.error('Delete failed:', err);
      alertC('Failed to delete surcharge');
    }
  };

  const surchargeTypeLabels = {
    fixed: 'Fixed $',
    per_sqft: 'Per Sqft',
    per_linear_ft: 'Per Linear Ft',
    percentage: 'Percentage',
    formula: 'Formula'
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'code', headerName: 'Code', width: 150 },
    { field: 'name', headerName: 'Name', width: 180 },
    { field: 'condition_attribute', headerName: 'Attribute', width: 120 },
    { field: 'condition_operator', headerName: 'Op', width: 80 },
    { field: 'condition_value', headerName: 'Value', width: 100 },
    { field: 'surcharge_type', headerName: 'Type', width: 100, renderCell: (params) => surchargeTypeLabels[params.value] || params.value },
    { field: 'surcharge_value', headerName: 'Amount', width: 90, renderCell: (params) => `$${parseFloat(params.value).toFixed(2)}` },
    { field: 'priority', headerName: 'Priority', width: 70 },
    { field: 'active', headerName: 'Active', width: 70, renderCell: (params) => params.value ? 'Yes' : 'No' }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Surcharges</Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" size="small" onClick={() => { setEditData(null); setOpenDialog(true); }}>
          Add Surcharge
        </Button>
        <Button variant="contained" color="secondary" size="small" onClick={() => { setEditData(selectedRow); setOpenDialog(true); }} disabled={!selectedRow}>
          Edit Surcharge
        </Button>
        <Button variant="outlined" color="error" size="small" onClick={handleDelete} disabled={!selectedRow}>
          Delete Surcharge
        </Button>
      </Stack>

      <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <span style={{ fontSize: '0.85em', color: '#555' }}>
          Surcharges add extra costs based on conditions like shape, glass type, or other attributes.
          Higher priority surcharges are evaluated first.
        </span>
      </Box>

      {loading ? <CircularProgress size={24} /> : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={surcharges.filter((r) => canSeeScope(r.applies_to))}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRow(params.row)}
          sx={{
            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#e3f2fd' },
            '& .MuiDataGrid-row': { cursor: 'pointer' }
          }}
          getRowClassName={(params) => params.row.id === selectedRow?.id ? 'Mui-selected' : ''}
        />
      )}

      <SurchargeDialog
        open={openDialog}
        data={editData}
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
    </Box>
  );
};

const SurchargeDialog = ({ open, data, onClose, onSave }) => {
  const { pickDefault, optionsForValue } = useProductScope();
  const [formData, setFormData] = useState({
    code: '', name: '', description: '',
    condition_attribute: '', condition_operator: 'equals', condition_value: '',
    surcharge_type: 'fixed', surcharge_value: 0, surcharge_formula: '',
    applies_to: 'all', priority: 0, active: true
  });

  useEffect(() => {
    if (data) {
      setFormData({
        code: data.code || '',
        name: data.name || '',
        description: data.description || '',
        condition_attribute: data.condition_attribute || '',
        condition_operator: data.condition_operator || 'equals',
        condition_value: data.condition_value || '',
        surcharge_type: data.surcharge_type || 'fixed',
        surcharge_value: data.surcharge_value || 0,
        surcharge_formula: data.surcharge_formula || '',
        applies_to: data.applies_to || pickDefault('all'),
        priority: data.priority || 0,
        active: data.active ?? true
      });
    } else {
      setFormData({
        code: '', name: '', description: '',
        condition_attribute: '', condition_operator: 'equals', condition_value: '',
        surcharge_type: 'fixed', surcharge_value: 0, surcharge_formula: '',
        applies_to: pickDefault('all'), priority: 0, active: true
      });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Surcharge' : 'Add Surcharge'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField label="Code" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} fullWidth placeholder="e.g., shape_trapezoid" />
            <TextField label="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth placeholder="e.g., Trapezoid Shape Surcharge" />
          </Stack>
          <TextField label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth multiline rows={2} />

          <Typography variant="subtitle2" sx={{ mt: 1, mb: -1 }}>Condition (when to apply)</Typography>
          <Stack direction="row" spacing={2}>
            <TextField label="Attribute" value={formData.condition_attribute} onChange={(e) => setFormData({ ...formData, condition_attribute: e.target.value })} fullWidth placeholder="e.g., shape, glass.strength" />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Operator</InputLabel>
              <Select value={formData.condition_operator} onChange={(e) => setFormData({ ...formData, condition_operator: e.target.value })} label="Operator">
                <MenuItem value="equals">Equals</MenuItem>
                <MenuItem value="not_equals">Not Equals</MenuItem>
                <MenuItem value="in">In List</MenuItem>
                <MenuItem value="not_in">Not In List</MenuItem>
                <MenuItem value="gt">Greater Than</MenuItem>
                <MenuItem value="lt">Less Than</MenuItem>
                <MenuItem value="gte">Greater/Equal</MenuItem>
                <MenuItem value="lte">Less/Equal</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Value" value={formData.condition_value} onChange={(e) => setFormData({ ...formData, condition_value: e.target.value })} fullWidth placeholder="e.g., trapezoid, Laminated" />
          </Stack>

          <Typography variant="subtitle2" sx={{ mt: 1, mb: -1 }}>Surcharge Amount</Typography>
          <Stack direction="row" spacing={2}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select value={formData.surcharge_type} onChange={(e) => setFormData({ ...formData, surcharge_type: e.target.value })} label="Type">
                <MenuItem value="fixed">Fixed $</MenuItem>
                <MenuItem value="per_sqft">Per Sqft</MenuItem>
                <MenuItem value="per_linear_ft">Per Linear Ft</MenuItem>
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="formula">Formula</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Value" type="number" value={formData.surcharge_value} onChange={(e) => setFormData({ ...formData, surcharge_value: parseFloat(e.target.value) || 0 })} fullWidth inputProps={{ step: '0.01' }} />
            {formData.surcharge_type === 'formula' && (
              <TextField label="Formula" value={formData.surcharge_formula} onChange={(e) => setFormData({ ...formData, surcharge_formula: e.target.value })} fullWidth placeholder="e.g., square * 1.5" />
            )}
          </Stack>

          <Stack direction="row" spacing={2}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Applies To</InputLabel>
              <Select value={formData.applies_to} onChange={(e) => setFormData({ ...formData, applies_to: e.target.value })} label="Applies To">
                {optionsForValue(formData.applies_to).map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Priority" type="number" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })} sx={{ width: 120 }} helperText="Higher = first" />
            <FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />} label="Active" />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SurchargesTab;
